import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import PanelSidebar from '../components/PanelSidebar';
import { ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import { formatMoney } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TamamlananSiparislerim = () => {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [siparisler, setSiparisler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seciliSiparis, setSeciliSiparis] = useState(null);
  const [aktifSekme, setAktifSekme] = useState('bekleyen');

  useEffect(() => {
    if (user?.cari_kodu) {
      fetchSiparisler();
    }
  }, [user]);

  const fetchSiparisler = async () => {
    try {
      const res = await axios.get(`${API}/panel/musteri-siparislerim/${encodeURIComponent(user.cari_kodu)}`);
      if (res.data.success) {
        setSiparisler(res.data.data);
      }
    } catch (err) {
      toast.error('Siparişler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleIptal = async (siparisId) => {
    if (!window.confirm('Bu siparişi iptal etmek istediğinizden emin misiniz?')) return;
    
    try {
      await axios.put(`${API}/panel/siparis-iptal/${siparisId}`);
      toast.success('Sipariş iptal edildi');
      fetchSiparisler();
    } catch (err) {
      toast.error('İptal edilemedi');
    }
  };

  const grupluSiparisler = siparisler.reduce((groups, s) => {
    // Transfer'leri atla (müşteri görmemeli)
    if (s.tip === 'transfer') return groups;
    const grupId = s.grup_siparis_id || s._id;
    if (!groups[grupId]) {
      groups[grupId] = {
        ana_siparis: s.is_parent ? s : null,
        alt_siparisler: [],
        toplam_urun: 0,
        toplam_tutar: 0
      };
    }
    
    if (s.is_parent) {
      groups[grupId].ana_siparis = s;
    } else {
      groups[grupId].alt_siparisler.push(s);
    }
    
    groups[grupId].toplam_urun += s.urunler?.length || 0;
    groups[grupId].toplam_tutar += s.toplam_tutar || 0;
    
    return groups;
  }, {});

  const grupListesi = Object.values(grupluSiparisler).filter(g => g.ana_siparis);

  const filtreliGruplar = useMemo(() => {
    return grupListesi.filter(g => {
      const s = g.ana_siparis;
      if (aktifSekme === 'bekleyen') return ['beklemede', 'transfer_beklemede'].includes(s.durum);
      if (aktifSekme === 'hazirlanan') return ['kismen_hazir', 'hazirlaniyor'].includes(s.durum);
      if (aktifSekme === 'tamamlanan') return ['tamamlandi', 'sevk_edildi', 'iptal', 'hazirlaniyor'].includes(s.durum);
      return true;
    });
  }, [grupListesi, aktifSekme]);

  const getDurumBadge = (durum) => {
    switch (durum) {
      case 'beklemede': return 'bg-indigo-100 text-indigo-700';
      case 'transfer_beklemede': return 'bg-amber-100 text-amber-700';
      case 'kismen_hazir': return 'bg-blue-100 text-blue-700';
      case 'hazirlaniyor': return 'bg-blue-100 text-blue-700';
      case 'tamamlandi': return 'bg-green-100 text-green-700';
      case 'iptal': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getDurumText = (durum) => {
    switch (durum) {
      case 'beklemede': return 'Beklemede';
      case 'transfer_beklemede': return 'Transfer Bekleniyor';
      case 'kismen_hazir': return 'Kısmen Hazır';
      case 'hazirlaniyor': return 'Hazırlanıyor';
      case 'tamamlandi': return 'Tamamlandı';
      case 'iptal': return 'İptal';
      default: return durum;
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <PanelSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

      <main className="flex-1 overflow-y-auto">
        <div className="bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-5">
            <h1 className="text-3xl font-bold text-gray-900">Siparişlerim</h1>
            <p className="text-sm text-gray-500 mt-1">{grupListesi.length} sipariş</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto p-6 space-y-4">
          {/* Sekme Butonları */}
          <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
            <div className="flex gap-3">
              <button
                onClick={() => setAktifSekme('bekleyen')}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  aktifSekme === 'bekleyen'
                    ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                📋 Bekleyen
              </button>
              <button
                onClick={() => setAktifSekme('hazirlanan')}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  aktifSekme === 'hazirlanan'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                🔄 Hazırlanan
              </button>
              <button
                onClick={() => setAktifSekme('tamamlanan')}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  aktifSekme === 'tamamlanan'
                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                ✅ Tamamlanan
              </button>
            </div>
          </div>

          {/* Tablo */}
          <div className="bg-slate-50 rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-700 text-white">
                <tr>
                    <th className="text-left py-2.5 px-2 text-sm">Sipariş No</th>
                    <th className="text-left py-2.5 px-2 text-sm">Tarih & Saat</th>
                    <th className="text-center py-2.5 px-2 text-sm">Ürün</th>
                    <th className="text-right py-2.5 px-2 text-sm">Tutar</th>
                    <th className="text-center py-2.5 px-2 text-sm">Durum</th>
                    <th className="text-center py-2.5 px-2 text-sm">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="text-center py-8">Yükleniyor...</td></tr>
                ) : filtreliGruplar.length > 0 ? (
                  filtreliGruplar.map((grup, idx) => {
                    const s = grup.ana_siparis;
                    const bekleyenTransfer = grup.alt_siparisler.filter(alt => alt.durum === 'transfer_beklemede').length;
                    
                    return (
                      <tr
                       key={s._id}
                       onClick={() => setSeciliSiparis(grup)}
                                className={`border-b hover:shadow-md hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 cursor-pointer transition-all ${
                          idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'
                        }`}
                        style={{borderLeft: '4px solid transparent'}}
                        onMouseEnter={(e) => e.currentTarget.style.borderLeft = '4px solid #6366f1'}
                      onMouseLeave={(e) => e.currentTarget.style.borderLeft = '4px solid transparent'}
                        >
                        <td className="py-3 px-3">
                      <span className="font-mono text-indigo-600 font-bold text-sm">{s.siparis_no}</span> 
                      </td>
                      <td className="py-3 px-3">
                      <span className="text-gray-700 text-sm font-medium">
                        {new Date(s.created_at).toLocaleDateString('tr-TR')}{' '}
                        <span className="text-gray-500 text-xs">
                          {new Date(s.created_at).toLocaleTimeString('tr-TR', {hour: '2-digit', minute: '2-digit'})}
                        </span>
                      </span>
                    </td>
                      <td className="py-3 px-3 text-center">
                      <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold">
                        {grup.toplam_urun} Ürün
                      </span>
                    </td>
                        <td className="py-3 px-3 text-right">
                        <span className="text-lg font-bold text-green-600">{formatMoney(grup.toplam_tutar)}</span>
                      </td>
                        <td className="py-2.5 px-3 text-center">
                          {bekleyenTransfer > 0 ? (
                            <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-xs font-semibold">
                              Transfer Bekleniyor ({bekleyenTransfer})
                            </span>
                          ) : (
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getDurumBadge(s.durum)}`}>
                              {getDurumText(s.durum)}
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <div className="flex gap-1 justify-center">
                            <button onClick={() => setSeciliSiparis(grup)} className="bg-indigo-500 hover:bg-indigo-600 text-white px-2 py-1 rounded text-xs">
                              Detay
                            </button>
                            {s.durum === 'beklemede' && (
                              <button onClick={() => handleIptal(s._id)} className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs">
                                İptal
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr><td colSpan="6" className="text-center py-12 text-gray-400">Sipariş bulunamadı</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {seciliSiparis && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSeciliSiparis(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-11/12 max-w-5xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-5">
                <h2 className="text-xl font-bold mb-3">📦 Sipariş: {seciliSiparis.ana_siparis.siparis_no}</h2>
                
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-white/10 rounded p-2">
                    <p className="text-xs opacity-80">Müşteri</p>
                    <p className="font-bold text-sm truncate">{seciliSiparis.ana_siparis.musteri_bilgileri?.ad || 'N/A'}</p>
                  </div>
                  <div className="bg-white/10 rounded p-2">
                    <p className="text-xs opacity-80">Tarih & Saat</p>
                    <p className="font-bold text-sm">
                      {new Date(seciliSiparis.ana_siparis.created_at).toLocaleDateString('tr-TR')} {' '}
                      {new Date(seciliSiparis.ana_siparis.created_at).toLocaleTimeString('tr-TR', {hour: '2-digit', minute: '2-digit'})}
                    </p>
                  </div>
                  <div className="bg-white/10 rounded p-2">
                    <p className="text-xs opacity-80">Teslimat</p>
                    <p className="font-bold text-sm truncate" title={seciliSiparis.ana_siparis.sevkiyat?.adres}>
                      {seciliSiparis.ana_siparis.sevkiyat?.adres?.substring(0, 30) || 'N/A'}
                    </p>
                  </div>
                  <div className="bg-white/10 rounded p-2">
                    <p className="text-xs opacity-80">Durum</p>
                    <p className="font-bold text-sm capitalize">{getDurumText(seciliSiparis.ana_siparis.durum)}</p>
                  </div>
                </div>
              </div>
            <div className="p-5 overflow-y-auto" style={{maxHeight: 'calc(90vh - 200px)'}}>
              <h3 className="text-sm font-bold text-gray-800 mb-3">🛒 Sipariş İçeriği</h3>
              <table className="w-full text-sm mb-4">
                <thead className="bg-slate-200 border-b">
                  <tr>
                    <th className="text-left py-2 px-2">Ürün</th>
                    <th className="text-center py-2 px-2">Adet</th>
                    <th className="text-right py-2 px-2">Fiyat</th>
                  </tr>
                </thead>
                <tbody>
                  {seciliSiparis.ana_siparis.urunler?.map((u, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="py-2 px-2">
                        <span className="font-medium">{u.urun_adi}</span>
                        <p className="text-xs text-gray-500">SKU: {u.sku}</p>
                      </td>
                      <td className="py-2 px-2 text-center font-bold">{u.miktar}</td>
                      <td className="py-2 px-2 text-right font-bold text-green-600">{formatMoney((u.miktar || 0) * (u.birim_fiyat || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {seciliSiparis.alt_siparisler.length > 0 && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-cyan-900 mb-3">🚚 Transfer Durumu</h4>
                  {seciliSiparis.alt_siparisler.map((alt, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-3 mb-2 flex justify-between items-center">
                      <div>
                        <p className="text-sm font-semibold">{alt.kaynak} → {alt.hedef}</p>
                        <p className="text-xs text-gray-600">{alt.urunler?.[0]?.urun_adi}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getDurumBadge(alt.durum)}`}>
                        {getDurumText(alt.durum)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-slate-50 p-4 border-t flex justify-end">
              <button onClick={() => setSeciliSiparis(null)} className="bg-slate-600 hover:bg-slate-700 text-white px-6 py-2 rounded-lg">Kapat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TamamlananSiparislerim;
